import { useEffect, useState } from 'react';

// Per-funding-source eligibility from the already-loaded PayPal JS SDK.
//
// isEligible() is a synchronous, merchant + buyer-region + device + currency aware
// check the SDK resolves at load. We surface it so callers can HIDE a funding
// source that is ineligible instead of rendering a dead button. On live, Venmo
// returns isEligible()===false (merchant configuration), so its option must not
// appear; PayPal is eligible and stays. This is general: any source can be
// ineligible for region/device/config, and this reports each independently.
//
// The SDK is loaded as a blocking <script> (index.html), so window.paypal is
// normally present before React mounts. We do not assume it: `ready` starts
// false with both sources hidden (no flash), and we poll briefly until the SDK
// is present, then resolve exactly once.

export interface PaypalEligibility {
  ready: boolean;
  paypal: boolean;
  venmo: boolean;
}

const INITIAL: PaypalEligibility = { ready: false, paypal: false, venmo: false };

export function usePaypalEligibility(): PaypalEligibility {
  const [state, setState] = useState<PaypalEligibility>(INITIAL);

  useEffect(() => {
    let cancelled = false;
    let tries = 0;

    const resolve = (): boolean => {
      const pp = (window as unknown as { paypal?: any }).paypal;
      if (!pp?.Buttons || !pp?.FUNDING) return false;

      // Constructing a Buttons instance purely to read isEligible() is
      // side-effect-free: no render(), no window.open, nothing to close (the
      // "P.onClose" teardown bug only comes from close()-ing an unrendered
      // instance, which we never do here). Discarded instances are GC'd.
      const eligible = (src: unknown): boolean => {
        try { return !!pp.Buttons({ fundingSource: src }).isEligible(); }
        catch { return false; }
      };

      if (!cancelled) {
        setState({
          ready: true,
          paypal: eligible(pp.FUNDING.PAYPAL),
          venmo: eligible(pp.FUNDING.VENMO),
        });
      }
      return true;
    };

    if (resolve()) return;

    const iv = setInterval(() => {
      tries += 1;
      // ~4s cap: if the SDK never loads, leave both hidden (better than a dead
      // button); Aeropay/bank do not depend on this and are unaffected.
      if (resolve() || tries > 40) clearInterval(iv);
    }, 100);

    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  return state;
}
